import { getTransactionColumns } from '@components/charts/membership/analytics-overview/transactionColumns'
import { useAnalytics } from '@contexts/AnalyticsContext'
import { MembershipTransaction } from '@models/membership/MembershipTransaction'
import { fetchMembershipTransactions } from '@services/membership/MembershipTransactionService'
import { useAppDispatch } from '@store/hooks'
import { upsertMembershipTransactions } from '@store/membership/MembershipTransactionReducer'
import { initialQuery, SBTable } from '@supplement-bacon/sb-table-filters'
import { useEffect, useRef, useState } from 'react'

const PREVIEW_ROW_COUNT = 5

/**
 * Compact "Last transactions" preview for the Revenue page: the N most recent
 * rows, no pagination/filters. It owns its rows (local state, newest request
 * wins) instead of sharing the drill-down's table state. Only the rows are also
 * upserted into the transaction slice's `items`, because the SBRM "View" action
 * reads the selected transaction from there (ticket 537, comments #8/#44);
 * `ids`, the query and the loading flag of `TransactionsPage` stay untouched.
 */
const LastTransactionsTable = () => {
  const dispatch = useAppDispatch()
  const [rows, setRows] = useState<MembershipTransaction[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const latest = useRef(0)

  const { selectedMembershipPrograms, isMembershipProgramReady } =
    useAnalytics()
  const programId = selectedMembershipPrograms?.[0]

  useEffect(() => {
    // Scoped to the page's program, like every block of the Revenue page.
    if (!isMembershipProgramReady) return

    const requestId = ++latest.current
    setIsLoading(true)

    fetchMembershipTransactions({
      ...initialQuery,
      columnKey: 'date',
      order: 'descend',
      pagination: { current: 1, pageSize: PREVIEW_ROW_COUNT },
      filters: {
        ...(programId !== undefined && { programs: [String(programId)] }),
      },
    })
      .then(({ data }) => {
        if (requestId !== latest.current) return
        setRows(data)
        dispatch(upsertMembershipTransactions(data))
      })
      .catch((error) => {
        if (requestId !== latest.current) return
        console.error('Error fetching the last transactions:', error)
        setRows([])
      })
      .finally(() => {
        if (requestId === latest.current) setIsLoading(false)
      })
  }, [dispatch, programId, isMembershipProgramReady])

  return (
    <SBTable
      columns={getTransactionColumns({ withActions: true, compact: true })}
      dataSource={rows}
      loading={isLoading}
      empty={{ description: 'No transactions found' }}
      pagination={false}
      tableLayout="fixed"
      rowKey="id"
    />
  )
}

export default LastTransactionsTable
